import React, { useEffect, useState } from 'react'
import store from '../store'
import { Provider } from 'react-redux'

import { openWidget, closeWidget, toggleWidget, toggleDisplay } from './Contexts/InputOutputHandler'
import { ContainerParent } from '../models/enums'
import getBaseAddress from '../utils/getBaseAddress'
import { fetchAvailableLanguagesAndGetCurrentLanguage } from '../utils/fetchAvailableLanguages'
import { getWidgetConfiguration } from '../utils/getConfigs'
import { deepMerge } from '../utils/getThemeColors'
import type { RetorikMainComponentProps, RetorikWidgetProps } from '../models/retorikTypes'

import { TelemetryProvider } from './Contexts/TelemetryContext'
import ErrorCollector from './Telemetry/ErrorCollector'
import GlobalProvider from './Contexts/GlobalProvider'
import ShadowRoot from './Common/ShadowRoot'
import LayoutHandler from './Common/LayoutHandler'
import RetorikConfigHandler from './Common/RetorikConfigHandler'

window.Retorik = {
  ...window.Retorik,
  openWidget,
  closeWidget,
  toggleWidget,
  toggleDisplay
}

const RetorikWidget = (props: RetorikMainComponentProps & RetorikWidgetProps & { fromRetorikFrameworkComponent?: boolean }): JSX.Element => {
  // --------- /!\ -----------
  // Ne mettre ici que de la logique spécifique au widget
  // Ce qui est commun avec RetorikAgent ou autres doit aller dans RetorikComposer ou ContextProvider
  // -------------------------

  const [processedProps, setProcessedProps] = useState<RetorikMainComponentProps & RetorikWidgetProps | undefined>(undefined)

  const fetchAndProcessConfiguration = async (baseAddress: string): Promise<void> => {
    const language = await fetchAvailableLanguagesAndGetCurrentLanguage(baseAddress)
    const fetchedConfiguration = await getWidgetConfiguration(baseAddress, language)
    const clonedProps = { ...props }
    // If we have a widget configuration in the props, let's use its data prior to the fetched ones
    if (clonedProps.widgetConfig) {
      setProcessedProps(fetchedConfiguration ? { ...clonedProps, widgetConfig: deepMerge(clonedProps.widgetConfig, fetchedConfiguration) } : clonedProps)
    } else {
      setProcessedProps(fetchedConfiguration ? { ...clonedProps, widgetConfig: fetchedConfiguration } : clonedProps)
    }

    props.renderCallback?.()
  }

  useEffect(() => {
    if (props.fromRetorikFrameworkComponent) {
      setProcessedProps(props)

      props.renderCallback?.()
    } else {
      fetchAndProcessConfiguration(getBaseAddress(props.addressData))
    }
  }, [])

  return (
    <Provider store={store}>
      <TelemetryProvider>
        <GlobalProvider instanceId={props.instanceId}>
          <ShadowRoot>
            <ErrorCollector>
              {processedProps ? (
                <LayoutHandler {...processedProps}>
                  <RetorikConfigHandler
                    {...{
                      ...processedProps,
                      width: 'full',
                      height: 'full',
                      parent: ContainerParent.widget
                    }}
                  />
                </LayoutHandler>
              ) : (
                <React.Fragment />
              )}
            </ErrorCollector>
          </ShadowRoot>
        </GlobalProvider>
      </TelemetryProvider>
    </Provider>
  )
}

export default RetorikWidget
